import { useState } from 'react';
import { usePwaInstalar } from '../../lib/pwaInstall';

/**
 * Convite de instalação do app do comprador — bloco EVIDENTE e persuasivo no
 * CORPO da área logada (não mais um ícone escondido no header). White-label:
 * "Instale o app da {Incorporadora}" e botão grande na cor da marca.
 *
 * A lógica (PROATIVA no mobile, standalone/appinstalled/dispensa/iOS) vem de
 * lib/pwaInstall.ts. No Android com instalador nativo o botão instala direto;
 * sem o evento, cai na instrução do menu; no iOS mostra "Compartilhar →
 * Adicionar à Tela de Início". Regras de memória (some quando instalado ou
 * dispensado; aparece no máx. 1x por dispositivo) inalteradas.
 */
export function InstalarAppBanner({
  slug,
  orgNome,
  corMarca,
}: {
  slug: string;
  orgNome: string | null | undefined;
  corMarca?: string | null;
}) {
  // Persistente: CTA proativo do comprador — reaparece a cada sessão até ser
  // instalado ou dispensado (não é o banner one-shot antigo).
  const { modo, instalarNativo, dispensar } = usePwaInstalar(`pwa_comprador_prompt_v2_${slug}`, { persistente: true });
  const [mostrarComo, setMostrarComo] = useState(false);
  if (!modo) return null;

  const nome = orgNome?.trim() || 'incorporadora';
  const acento = corMarca && /^#[0-9a-fA-F]{6}$/.test(corMarca) ? corMarca : 'var(--brand, var(--accent))';

  function aoTocarInstalar() {
    if (instalarNativo) void instalarNativo();
    else setMostrarComo(true);
  }

  return (
    <div
      data-testid="comprador-instalar"
      role="dialog"
      aria-live="polite"
      className="relative mb-5 overflow-hidden rounded-3xl border p-5"
      style={{ borderColor: 'var(--brand-line, var(--line))', background: 'var(--brand-soft, var(--accent-soft))' }}
    >
      <button
        type="button"
        onClick={dispensar}
        aria-label="Agora não"
        data-testid="comprador-instalar-fechar"
        className="absolute right-3 top-3 rounded-full p-1.5 text-ink-3 transition-colors hover:bg-black/5 hover:text-ink"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      <div className="flex items-start gap-3.5 pr-6">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white"
          style={{ background: acento }}
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
            <rect x="5" y="2.5" width="14" height="19" rx="3" stroke="currentColor" strokeWidth="1.7" />
            <path d="M12 7.5v6m0 0l-2.5-2.5M12 13.5l2.5-2.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.5 18.5h3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-[19px] font-medium leading-snug text-ink">Instale o app da {nome} no seu celular</h2>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
            Acesso rápido pela tela inicial, sem link, sem senha: é só tocar no ícone.
          </p>
        </div>
      </div>

      {/* iOS: sem instalador nativo — mostra o passo a passo já. */}
      {modo === 'ios' ? (
        <ol className="mt-4 space-y-2" data-testid="comprador-instalar-ios">
          <Passo n="1" acento={acento} icone={<IconeCompartilhar />}>
            Toque em <strong>Compartilhar</strong> na barra do navegador.
          </Passo>
          <Passo n="2" acento={acento} icone={<IconeAdicionar />}>
            Escolha <strong>Adicionar à Tela de Início</strong>.
          </Passo>
        </ol>
      ) : mostrarComo ? (
        <ol className="mt-4 space-y-2" data-testid="comprador-instalar-como">
          <Passo n="1" acento={acento} icone={<IconeMenu />}>
            Abra o menu <strong>(⋮)</strong> do navegador, no canto superior.
          </Passo>
          <Passo n="2" acento={acento} icone={<IconeBaixar />}>
            Toque em <strong>Instalar app</strong> (ou <strong>Adicionar à tela inicial</strong>).
          </Passo>
        </ol>
      ) : (
        <button
          type="button"
          onClick={aoTocarInstalar}
          data-testid="comprador-instalar-acao"
          className="mt-4 w-full rounded-2xl py-3.5 text-[15px] font-semibold text-white shadow-sm transition-transform active:scale-[0.99]"
          style={{ background: acento }}
        >
          Instalar agora
        </button>
      )}
    </div>
  );
}

function Passo({ n, acento, icone, children }: { n: string; acento: string; icone: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3 rounded-xl border border-line bg-surface px-3.5 py-2.5">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white" style={{ background: acento }}>
        {n}
      </span>
      <span className="shrink-0 text-ink-2" aria-hidden="true">{icone}</span>
      <span className="min-w-0 flex-1 text-[13px] text-ink-2">{children}</span>
    </li>
  );
}

function IconeCompartilhar() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M12 3v12m0-12l-3.5 3.5M12 3l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 11H5a2 2 0 00-2 2v6a2 2 0 002 2h14a2 2 0 002-2v-6a2 2 0 00-2-2h-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconeAdicionar() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 8.5v7M8.5 12h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconeMenu() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" />
    </svg>
  );
}
function IconeBaixar() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M12 3v11m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
